import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from './PageLayout';
import AuthService from '../services/AuthService';
import ClientService from '../services/ClientService';
import { T, useT } from '../Translation/index';
import Loader from './Loader';
import { useLicenseKey } from '../hooks/useLicenseDetails';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: #f8f9fa;
  padding: 24px 16px;
  @media (max-width: 679px) {
    padding: 16px 12px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8ecf1;
  padding: 36px 28px;
  width: 85%;
  max-width: 85%;
  @media (max-width: 679px) {
    padding: 28px 20px;
    border-radius: 14px;
  }
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const LogoIcon = styled.img`
  height: 72px;
  margin: 0 auto 20px;
  display: block;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #191c1d;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #6c757d;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Input = styled.input`
  padding: 13px 14px;
  border: 1px solid #dde1e6;
  border-radius: 10px;
  font-size: 16px;
  color: #191c1d;
  background: #fafbfc;
  text-align: center;
  letter-spacing: 1.5px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: #adb5bd;
    letter-spacing: 0;
  }
`;

const Button = styled.button`
  padding: 13px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: #1565c0;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: #b0c4de;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #c62828;
  font-size: 13px;
  text-align: center;
  padding: 10px;
  background: #ffebee;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  font-size: 13px;
  text-align: center;
  padding: 10px;
  background: #e8f5e9;
  border-radius: 8px;
`;

const InvitationPage: React.FC = () => {
  const [inputcode, setInputCode] = useState('');
  const [, setLicenseKeyInLocalStorage] = useLicenseKey();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const t = useT();

  useEffect(() => {
    const validateCode = async () => {
      if (!code) {
        setError('請輸入邀請碼');
        return;
      }
      setIsLoading(true);
      try {
        const authService = AuthService.getInstance();
        const clientService = ClientService.getInstance();
        await authService.getAuthorization();
        const result = await clientService.handleValidation(code);

        if (!result.success) {
          setError(result.error || '邀請碼驗證失敗');
        } else {
          setLicenseKeyInLocalStorage(result.licence!);
          navigate('/monitor');
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setError(error.response.data.msg || '邀請碼驗證失敗');
        } else {
          setError('邀請碼驗證失敗');
        }
      } finally {
        setIsLoading(false);
      }
    };
    validateCode();
  }, [code, navigate, setLicenseKeyInLocalStorage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputcode.trim()) {
      setError('請輸入邀請碼');
      return;
    }
    setIsLoading(true);
    try {
      const authService = AuthService.getInstance();
      const clientService = ClientService.getInstance();
      await authService.getAuthorization();
      const result = await clientService.handleValidation(inputcode);
      if (!result.success) {
        setError(result.error || '邀請碼驗證失敗');
      } else {
        setLicenseKeyInLocalStorage(result.licence!);
        navigate('/monitor');
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 400) {
          setError('邀請碼驗證失敗');
        } else {
          setError(error.response.data.msg || '登入失败');
        }
      } else {
        setError('邀請碼驗證失敗');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      <Container>
        <Card>
          <LogoArea>
            <LogoIcon src={require('../assets/charlycam.png')} alt="App Icon" />
            <Title><T id="invitation" /></Title>
            <Subtitle><T id="invitation_subtitle" /></Subtitle>
          </LogoArea>

          {isLoading ? (
            <Loader />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Input
                type="text"
                placeholder={t('invitation_code_placeholder')}
                value={inputcode}
                onChange={(e) => setInputCode(e.target.value)}
                autoFocus
              />
              <Button type="submit" disabled={!inputcode.trim()}>
                <T id="submit" />
              </Button>
              {error && <ErrorMessage><T id={error} /></ErrorMessage>}
              {code && !error && <SuccessMessage><T id="verifying" /></SuccessMessage>}
            </Form>
          )}
        </Card>
      </Container>
    </PageLayout>
  );
};

export default InvitationPage;
